import StepGradient from './step-gradient';
import GradientFlow from './gradient-flow';
import Chaos from './chaos';
import WavyWaves from './wavy-waves';
import AbstractShape from './abstract-shape';
import AestheticFluid from './aesthetic-fluid';
import SwirlingCurves from './swirling-curves';
import BlurDot from './blur-dot';
import BigBlob from './big-blob';
import RandomCubes from './random-cubes';
import CosmicRipples from './cosmic-ripples';
import DragonScales from './dragon-scales';
import TurbulentSpill from './turbulent-spill';
import PlasmaWaves from './plasma-waves';
import SinCos3D from './sincos-3d';
import LightLine from './light-line';
import Light from './light';

// Two-tier registry: shader programs and metadata
const registry = new Map();
const metaRegistry = new Map();

export function registerShader(name, shader) {
  if (!name || !shader || !shader.frag) return;
  registry.set(String(name), shader);
}

export function registerShaderModule(mod) {
  if (!mod || !mod.name || !mod.shader || !mod.shader.frag) return;
  // store program
  registry.set(String(mod.name), mod.shader);
  // normalize and augment schema with a common random seed control if missing
  const originalSchema = Array.isArray(mod.schema) ? mod.schema : [];
  const hasSeed = originalSchema.some((s) => s && (s.key === 'seed' || s.key === 'u_seed'));
  const schema = hasSeed
    ? originalSchema
    : [
        ...originalSchema,
        {
          key: 'seed',
          type: 'number',
          label: 'Seed',
          min: 0,
          max: 9999,
          step: 1,
          default: 0,
        },
      ];
  // store metadata
  metaRegistry.set(String(mod.name), {
    name: mod.name,
    schema,
    examples: mod.examples || [],
  });
}

export function getShader(name) {
  return registry.get(String(name)) || null;
}

export function getShaderMeta(name) {
  return metaRegistry.get(String(name)) || null;
}

export function listShaders() {
  return Array.from(metaRegistry.values());
}

// Helpers to parse/build dynamic-shader strings
export function parsePropsString(str) {
  const props = {};
  if (!str) return props;
  const parts = str
    .split(';')
    .map((s) => s.trim())
    .filter(Boolean);
  for (const part of parts) {
    const i = part.indexOf('=');
    if (i === -1) continue;
    const key = part.slice(0, i).trim();
    const raw = part.slice(i + 1).trim();
    if (!key) continue;
    if (key.toLowerCase() === 'colors') {
      props.colors = raw
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      continue;
    }
    props[key] = raw;
  }
  return props;
}

export function parseShaderString(value) {
  const m = String(value || '').match(/dynamic-shader\s*\(\s*([^,\s)]+)\s*(?:,\s*(.*))?\)\s*$/i);
  if (!m) return null;
  const name = m[1].trim();
  const propsStr = (m[2] || '').trim();
  return { name, props: parsePropsString(propsStr) };
}

export function toShaderString(name, props) {
  const parts = [];
  if (Array.isArray(props.colors) && props.colors.length) {
    parts.push(`colors=${props.colors.join(',')}`);
  }
  for (const [k, v] of Object.entries(props)) {
    if (k === 'colors') continue;
    if (v === undefined || v === null || v === '') continue;
    parts.push(`${k}=${v}`);
  }
  const propsJoined = parts.join('; ');
  return `dynamic-shader(${name}${propsJoined ? ', ' + propsJoined : ''})`;
}

// Register built-ins
registerShaderModule(Chaos);
registerShaderModule(WavyWaves);
registerShaderModule(AbstractShape);
registerShaderModule(AestheticFluid);
registerShaderModule(SwirlingCurves);
registerShaderModule(StepGradient);
registerShaderModule(GradientFlow);
registerShaderModule(BlurDot);
registerShaderModule(BigBlob);
registerShaderModule(RandomCubes);
registerShaderModule(CosmicRipples);
registerShaderModule(DragonScales);
registerShaderModule(TurbulentSpill);
registerShaderModule(PlasmaWaves);
registerShaderModule(SinCos3D);
registerShaderModule(LightLine);
registerShaderModule(Light);
